// features/events/eventsApiSlice.js
import { apiSlice } from "../../app/api/apiSlice";

export const eventsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ---- READ ----
    getEvents: builder.query({
      query: () => "/events",
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((e) => ({ type: "Event", id: e._id || e.id })),
              { type: "Event", id: "LIST" },
            ]
          : [{ type: "Event", id: "LIST" }],
    }),
    getMemberEvents: builder.query({
      query: () => "/events/member",
      providesTags: [{ type: "Event", id: "MEMBER" }],
    }),
    getEvent: builder.query({
      query: (id) => `/events/${id}`,
      providesTags: (_res, _err, id) => [{ type: "Event", id }],
    }),

    // ---- CREATE ----
    createEvent: builder.mutation({
      query: (event) => ({
        url: "/events",
        method: "POST",
        body: event,
      }),
      invalidatesTags: [
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ---- UPDATE ----
    updateEvent: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/events/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (res, _err, { id }) => {
        const tags = [
          { type: "Event", id },
          { type: "Event", id: "LIST" },
          { type: "Event", id: "MEMBER" },
        ];
        // Bulk-Preispropagation hat Buchungen serverseitig aktualisiert → Cache invalidieren
        if (res?.pricingPropagation) {
          tags.push({ type: "EventRegistration", id: `LIST-${id}` });
        }
        return tags;
      },
    }),
    updateEventVisibility: builder.mutation({
      query: ({ id, isPublic }) => ({
        url: `/events/${id}/visibility`,
        method: "PATCH",
        body: { isPublic },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ---- DELETE ----
    deleteEvent: builder.mutation({
      query: (id) => ({
        url: `/events/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, id) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ---- COMMENTS ----
    addComment: builder.mutation({
      query: ({ id, text }) => ({
        url: `/events/${id}/comments`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: "Event", id }],
    }),
    deleteComment: builder.mutation({
      query: ({ id, commentId }) => ({
        url: `/events/${id}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: "Event", id }],
    }),

    // ---- ATTENDEES (testing/event lists) ----
    addAttendees: builder.mutation({
      query: ({ id, memberIds }) => ({
        url: `/events/${id}/attendees`,
        method: "POST",
        body: { memberIds },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    removeAttendees: builder.mutation({
      query: ({ id, memberIds }) => ({
        url: `/events/${id}/attendees`,
        method: "DELETE",
        body: { memberIds },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    replaceAttendees: builder.mutation({
      query: ({ id, memberIds }) => ({
        url: `/events/${id}/attendees`,
        method: "PUT",
        body: { memberIds },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    graduateAttendees: builder.mutation({
      query: (id) => ({
        url: `/events/${id}/graduate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
      ],
    }),

    // ---- PARTICIPANTS BY CRITERIA ----
    previewParticipantsByCriteria: builder.mutation({
      query: ({ id, criteria }) => ({
        url: `/events/${id}/participants/by-criteria/preview`,
        method: "POST",
        body: criteria,
      }),
    }),
    addParticipantsByCriteria: builder.mutation({
      query: ({ id, criteria }) => ({
        url: `/events/${id}/participants/by-criteria`,
        method: "POST",
        body: criteria,
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ---- MEMBER PARTICIPATION ----
    registerParticipation: builder.mutation({
      query: ({ id, participants, all = false, replace = true }) => ({
        url: `/events/${id}/participation`,
        method: "POST",
        body: { participants, all, replace },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    // RSVP (Zusage/Absage) for testing event attendees
    submitRsvp: builder.mutation({
      query: ({ id, memberId, action, photoOptOut = false }) => ({
        url: `/events/${id}/rsvp`,
        method: "POST",
        body: { memberId, action, photoOptOut },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "MEMBER" },
      ],
    }),

    // ---- EVENT MEDIA ----
    getEventMedia: builder.query({
      query: (id) => `/events/${id}/media`,
      providesTags: (_res, _err, id) => [{ type: "Event", id }],
    }),
    uploadEventMedia: builder.mutation({
      query: ({
        id,
        files = [],
        isVisibleToMembers = false,
        replaceMediaId = "",
      }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        formData.append("isVisibleToMembers", String(Boolean(isVisibleToMembers)));
        if (replaceMediaId) {
          formData.append("replaceMediaId", String(replaceMediaId));
        }
        return {
          url: `/events/${id}/media`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    updateEventMediaVisibility: builder.mutation({
      query: ({ id, mediaId, isVisibleToMembers }) => ({
        url: `/events/${id}/media/${mediaId}/visibility`,
        method: "PATCH",
        body: { isVisibleToMembers },
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "MEMBER" },
      ],
    }),
    downloadEventMedia: builder.mutation({
      query: ({ id, mediaId }) => ({
        url: `/events/${id}/media/${mediaId}/download`,
        method: "GET",
        responseHandler: (response) => response.blob(),
      }),
    }),
    deleteEventMedia: builder.mutation({
      query: ({ id, mediaId }) => ({
        url: `/events/${id}/media/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "MEMBER" },
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetMemberEventsQuery,
  useGetEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useUpdateEventVisibilityMutation,
  useDeleteEventMutation,
  useAddCommentMutation,
  useDeleteCommentMutation,
  useAddAttendeesMutation,
  useRemoveAttendeesMutation,
  useReplaceAttendeesMutation,
  useGraduateAttendeesMutation,
  usePreviewParticipantsByCriteriaMutation,
  useAddParticipantsByCriteriaMutation,
  useRegisterParticipationMutation,
  useSubmitRsvpMutation,
  useGetEventMediaQuery,
  useUploadEventMediaMutation,
  useUpdateEventMediaVisibilityMutation,
  useDownloadEventMediaMutation,
  useDeleteEventMediaMutation,
} = eventsApiSlice;
